import React, { useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, useHistory } from 'react-router-dom';
import Button from '@cpg-its/ads.common.button';
import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import { Hidden } from '@material-ui/core';
import { TcsmStates } from '../../../../../root/tcsm-model';
import { NewHireProps } from '../../../models/arc-epi-props';
import { Employment, Position } from '../../employment/employment-model';
import { DividerSection } from '../../../../../../stories/divider-section';
import { LabelAndValue } from '../../../../../global/components/data-display/label-value';
import { getFormattedDate } from '../../../../../global/helpers/date-helpers';
import { AnnualEnrollmentContext, AnnualEnrollmentKey } from '../../../../annual-enrollment/models/annual-enrollment-models';
import { setCurrentNewHireBenefitEnrollmentStep } from '../slice/new-hire-enrollment-slices';
import { BenefitEnrollmentStep } from '../models/pension-enrollment-models';
import { useMemberEmployment } from './hooks/useMemberEmployment';
import { useMemberCompensation } from './hooks/useMemberCompensation';
import { useBasePath } from '../../../../../global/hooks/useBasePath';
import { AccountInfoRoutes } from '../../../../account-info/constants/routes';
import { ArcRoutes } from '../../../constants/routes';
import { ButtonWrapper } from '../../../../../global/components/layout/button-wrapper';
import { PageHeader } from '../../../../../global/components/layout/page-header';
import { LabelSettingProps } from '../../../../../global/models/epi-model';
import { YesNoIndicators } from '../../../../../global/models/api-model';
import { GuidedProcessSupportBlock } from '../../../../../global/components/guided-process-support-block';
import { GpsType } from '../../../../../global/components/layout/layout-models';
import { SideBarContent } from '../../../../../global/components/layout/sidebar-content';
import { getGpsType } from '../../../../../global/helpers/gps-helper';
import { DemographicsContext } from '../../../../../global/models/demographics-model';
import { NewHireEnrollmentRoutes } from '../models/new-hire-routes';
import { useCurrentGhlFormId } from '../hooks/useCurrentGhlFormId';
import { useMemberCoverageState } from '../hooks/useMemberCoverageState';
import { useMemberPensionEnrollmentInfo } from './hooks/useMemberPensionEnrollmentInfo';
import { MemberCompensation } from '../models/new-hire-enrollment-models';
import { shouldMemberNavigateCoverageStep, shouldMemberNavigateRetirementStep } from './benefit-enrollment/helper/member-benefit-enrollment-helper';
import { MemberNewHireEnrollmentCoverage } from './models/member-coverage-model';
import { PensionEnrollmentInfo } from '../../pension-enrollment/models/pension-enrollment-model';

interface Props {	    
    basePath?: string;
}

export function MemberEmploymentCompensation(props: Props) {    
	const app = useContext(DemographicsContext);
	const gpsItem = getGpsType(GpsType.MEMBER_NEW_HIRE_EMPLOYMENT, app);

	const history = useHistory();
    const { basePath } = props;
    const enrollmentContext = useContext(AnnualEnrollmentContext);

	const labelSettings = useSelector<TcsmStates, LabelSettingProps>(state => state?.epiProps?.labelSettingState?.epiContent);
	
    const newHirePropsLoaded = useSelector<TcsmStates, boolean>(state => state?.epiProps?.newHireSettingsState?.contentLoaded);
    const newHireProps = useSelector<TcsmStates, NewHireProps>(state => state?.epiProps?.newHireSettingsState?.epiContent);
    
    const memberCurrentEmploymentIdLoaded = useSelector<TcsmStates, boolean>(state => state?.newHireEnrollmentState?.memberCurrentEmploymentIdLoaded);

	const guid = useSelector<TcsmStates, string>(state => state.authenticatedState?.clientGuid);
	const currentForm = useCurrentGhlFormId();	
	const memberCoverageLoaded = useMemberCoverageState(guid, currentForm?.id);	
    const memberEmploymentLoaded = useMemberEmployment();
    const memberCompensationLoaded = useMemberCompensation();
    const memberPensionEnrollmentInfoLoaded = useMemberPensionEnrollmentInfo();	
	
    const memberEmployment = useSelector<TcsmStates, Employment>(state => state?.newHireEnrollmentState?.memberEmployment);
    const memberCompensationState = useSelector<TcsmStates, MemberCompensation>(state => state?.newHireEnrollmentState?.memberCompensation);	
	const memberCoverageState = useSelector<TcsmStates, MemberNewHireEnrollmentCoverage>(state => state.newHireEnrollmentState?.memberCoverageState);
	const memberPensionEnrollmentInfo = useSelector<TcsmStates, PensionEnrollmentInfo>(state => state?.newHireEnrollmentState?.memberPensionInfo);

	const memberCompensation = memberCompensationState?.compensations[0];
	
    const dispatch = useDispatch();
	
	const newHireEnrollmentRoot = useBasePath(`${AccountInfoRoutes.MyCpgRoot}${ArcRoutes.MemberNewHireEnrollment}`);
    const memberNewHireEnrollmentBenefitEnrollmentRoot = `${newHireEnrollmentRoot}${NewHireEnrollmentRoutes.BenefitEnrollment}`;

	const prevStep = `${basePath}${AccountInfoRoutes.RelationshipsSegment}`;
	const stepRetirement = `${memberNewHireEnrollmentBenefitEnrollmentRoot}${NewHireEnrollmentRoutes.Retirement}`;
	const stepCoverage = `${memberNewHireEnrollmentBenefitEnrollmentRoot}${NewHireEnrollmentRoutes.Coverage}`;
	const stepReview = `${memberNewHireEnrollmentBenefitEnrollmentRoot}${NewHireEnrollmentRoutes.Review}`;

    useEffect(() => {		
		if (enrollmentContext === AnnualEnrollmentKey.newHireEnrollment)
			dispatch(setCurrentNewHireBenefitEnrollmentStep(BenefitEnrollmentStep.EMPLOYMENT));
	}, []);

	function prevStepOnClick(){
		history.push(prevStep);
	}

	function continueToNextStepText() : string {
		return newHireProps?.continueToEmployerPaidBenefitsButtonText;
	}

	function nextStepOnClick(){
		if (enrollmentContext === AnnualEnrollmentKey.newHireEnrollment) {
			if (shouldMemberNavigateRetirementStep(memberPensionEnrollmentInfo, memberCompensationState, memberEmployment)){
				history.push(stepRetirement);			
				return;
			}

			if (shouldMemberNavigateCoverageStep(memberCompensationState, memberEmployment, memberCoverageState)){
				history.push(stepCoverage);			
				return;
			}
			
			history.push(stepReview);			
		}
	}	

	const positionView = (position: Position) => {
        return (
            <>
                <p>{position?.position_title}</p>
                <p className='grey-caption'>{getFormattedDate(position?.start_date)} - {getFormattedDate(position?.end_date) || newHireProps?.presentLabel}</p>
            </>
        );
    };

    if (!memberCurrentEmploymentIdLoaded || !basePath){
        return <Redirect to={newHireEnrollmentRoot} />;
    }

	if (!memberEmploymentLoaded || !memberCompensationLoaded || !newHirePropsLoaded || !memberCoverageLoaded || !memberPensionEnrollmentInfoLoaded) {
		return <LoadingCircle />;
	}

    return (
        <>
			{
				memberEmployment &&
				<>
					<PageHeader headerText={newHireProps?.employmentCompensationHeader} description={newHireProps?.employmentCompensationDescription} />
					<Hidden mdUp>
						<SideBarContent>
							<GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
						</SideBarContent>
					</Hidden>
					<DividerSection sectionText={`${getFormattedDate(memberEmployment?.current_position_title_start_date)} - ${newHireProps?.presentLabel}`}>					
						<h4>{newHireProps?.employmentTitle}</h4>
						<LabelAndValue label={newHireProps?.institutionLabel} value={memberEmployment?.employer_name} />
						<LabelAndValue 
							label={newHireProps?.positionLabel} 
							value={<>
                                {
                                    memberEmployment?.position_titles?.map((positionItem : Position) => {
                                        return positionView(positionItem);
                                    })
                                }
                            </>} 
						/>		
						<LabelAndValue label={newHireProps?.addressLabel} 
								value={
								<>
									<div>{`${memberEmployment?.employer_street_address},`}</div>
									<div>{`${memberEmployment?.employer_location}, ${memberEmployment?.employer_country}`}</div>
								</>
							} />
						<h4>{newHireProps?.compensationTitle}</h4>
						<LabelAndValue label={newHireProps?.baseSalaryLabel} value={memberCompensation?.cash_salary} tooltipMessage={newHireProps?.baseSalaryTooltip} />
						<LabelAndValue label={newHireProps?.cashHousingAllowanceLabel} value={memberCompensation?.housing_cash_allowance} tooltipMessage={newHireProps?.cashHousingAllowanceTooltip} />
						<LabelAndValue label={newHireProps?.employerProvidedHousingLabel} value={memberCompensation?.housing_provided === YesNoIndicators.Yes ? labelSettings?.yesText : labelSettings?.noText } tooltipMessage={newHireProps?.employerProvidedHousingTooltip} />
						<LabelAndValue label={newHireProps?.erPlanContributionsLabel} value={memberCompensation?.er_plan_contributions} tooltipMessage={newHireProps?.erPlanContributionsTooltip} />
						<LabelAndValue label={newHireProps?.dbHousingValueLabel} value={memberCompensation?.calculated_housing} tooltipMessage={newHireProps?.dbHousingValueHelper} />
						<LabelAndValue label={newHireProps?.dcHousingValueLabel} value={memberCompensation?.calculated_housing_dc_rsvp} tooltipMessage={newHireProps?.dcHousingValueHelper} />
						<LabelAndValue label={newHireProps?.dbCompTotalLabel} value={memberCompensation?.tac} tooltipMessage={newHireProps?.dbCompTotalHelper} />
						<LabelAndValue label={newHireProps?.dcCompTotalLabel} value={memberCompensation?.tac_dc_rsvp} tooltipMessage={newHireProps?.dcCompTotalHelper} />
					</DividerSection>
					<ButtonWrapper flexEnd>
						<Button type='button' buttonStyle='tertiary' text={newHireProps?.backToRelationshipButtonText} backgroundColor='#808080' onClick={prevStepOnClick} />
						<Button type='button' buttonStyle='primary' text={continueToNextStepText()} backgroundColor='#0000FF' onClick={nextStepOnClick} />
					</ButtonWrapper>
				</>
			}
		</>
    );
}

MemberEmploymentCompensation.defaultProps = {
	basePath: null,
};
